import { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import { api, formatDate, resolveImg } from "@/lib/api";
import DOMPurify from "dompurify";
import { useSocialEmbeds } from "@/lib/socialEmbeds";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Clock, CheckCircle2, BadgeCheck } from "lucide-react";
import ShareBar from "@/components/ShareBar";
import RichText from "@/components/RichText";
import VideoEmbed from "@/components/VideoEmbed";
import ReactionBar from "@/components/ReactionBar";
import ReadingProgressBar from "@/components/ReadingProgressBar";
import ArticleSidebar from "@/components/ArticleSidebar";
import UpNextWidget from "@/components/UpNextWidget";
import AdSlot, { ADS_ENABLED } from "@/components/ads/AdSlot";
import TitanPulseStrip from "@/components/TitanPulseStrip";
import { TitanTemperature } from "@/components/SignatureWidgets";
import AuthorBioCard from "@/components/AuthorBioCard";
import QuickHitsBox from "@/components/QuickHitsBox";
import FAQBox from "@/components/FAQBox";
import { track } from "@/lib/analytics";

const FALLBACK = "https://images.pexels.com/photos/264305/pexels-photo-264305.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Slug for /tag/:slug topic pages — must match backend routers/tags.py tag_slug().
const tagSlug = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Visually separate the photo credit from the caption inside figcaptions.
// The legally required attribution text is preserved verbatim — this only
// wraps it in a span so CSS can render it smaller/lighter below the caption.
const CREDIT_RE = /(Mandatory Credit:|Photo Credit:|PHOTO:|Photo:|Credit:|Photo by |Image credit:)/i;
const splitCaptionCredit = (html) => {
  if (!html || !/figcaption/i.test(html)) return html;
  try {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll("figcaption").forEach((fc) => {
      if (fc.querySelector(".ts-photo-credit")) return;
      const sm = fc.querySelector("small");
      if (sm) { sm.classList.add("ts-photo-credit"); return; }
      const full = fc.textContent || "";
      const m = full.match(CREDIT_RE);
      if (!m) return;
      if (m.index === 0) { fc.classList.add("ts-photo-credit-only"); return; }
      const caption = full.slice(0, m.index).trim();
      const credit = full.slice(m.index).trim();
      fc.textContent = "";
      const capSpan = document.createElement("span");
      capSpan.textContent = caption;
      const credSpan = document.createElement("span");
      credSpan.className = "ts-photo-credit";
      credSpan.textContent = credit;
      fc.appendChild(capSpan);
      fc.appendChild(credSpan);
    });
    return div.innerHTML;
  } catch {
    return html;
  }
};

export default function NewsDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [article, setArticle] = useState(location.state?.article || null);
  const [loading, setLoading] = useState(!article);
  const articleBodyRef = useRef(null);

  // Redirect old UUID URLs to the SEO-friendly slug URL (canonical).
  // Uses replace so the UUID URL is removed from browser history + linked-out.
  // GUARD: only redirect when the URL id actually matches the loaded
  // article's UUID (canonical UUID form). Without this guard, clicking a
  // related-article link would cause a snap-back: the URL changes to the
  // new slug, but `article` state still holds the previous article — and
  // this effect would fire before the refetch, redirecting the URL back
  // to the previous article. That was the "stays on same article" bug.
  useEffect(() => {
    if (!article) return;
    if (article.id === id && article.slug && article.slug !== id) {
      navigate(`/news/${article.slug}`, { replace: true, state: { article } });
    }
  }, [article, id, navigate]);

  const [comments, setComments] = useState([]);
  const [author, setAuthor] = useState("");
  const [body, setBody] = useState("");
  const [related, setRelated] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [authorProfile, setAuthorProfile] = useState(null);

  // Upgrade any social-embed blockquotes rendered inside the article body.
  useSocialEmbeds(article?.id);

  // Reading time (minutes) — computed from blocks/body.
  const readTime = (() => {
    if (!article) return 0;
    const wordsFromBlocks = (article.blocks || [])
      .map((b) => {
        if (b.type === "image" || b.type === "video") return b.caption || "";
        if (b.type === "html" && b.html) {
          if (typeof document !== "undefined") {
            const div = document.createElement("div");
            div.innerHTML = b.html;
            return div.textContent || "";
          }
          return "";
        }
        return b.text || "";
      })
      .join(" ");
    const allText = `${article.body || ""} ${wordsFromBlocks}`;
    const wc = allText.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(wc / 220));
  })();

  useEffect(() => {
    // Bug guard: when navigating from /news/A → /news/B, the article state
    // still holds article A. If we skip the fetch (as the old code did with
    // `if (article) return;`), the page shows the previous article's body
    // under the new URL. Refetch whenever the URL id/slug doesn't match the
    // article we currently have.
    if (article && (article.slug === id || article.id === id)) {
      setLoading(false);
      return;
    }
    // If the click passed the new article via router state, use it instantly
    // (no fetch flash). This makes related-article clicks feel instant.
    const stateArticle = location.state?.article;
    if (stateArticle && (stateArticle.slug === id || stateArticle.id === id)) {
      setArticle(stateArticle);
      setLoading(false);
      // Kick a background refetch to hydrate any missing fields (blocks, body).
      (async () => {
        try {
          const { data } = await api.get(`/news/${id}`);
          if (data) setArticle(data);
        } catch { /* keep state version */ }
      })();
      return;
    }
    // Clear the stale article immediately so the loading state shows and the
    // reader never sees the previous story's body under a new URL.
    setArticle(null);
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/news/${id}`);
        setArticle(data);
      } catch {
        setArticle(null);
      } finally {
        setLoading(false);
      }
    })();
    // Intentionally NOT depending on `article` — that would infinite-loop
    // (fetch → setArticle → effect fires → …). We only re-run when the URL
    // changes, and we compare against the current article inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/news/${id}/related`, { params: { limit: 3 } });
        setRelated(data || []);
      } catch { /* hide */ }
    })();
    // Titans players mentioned in the article → internal /player/:id links.
    (async () => {
      try {
        const { data } = await api.get(`/news/${id}/mentions`, { params: { limit: 6 } });
        setMentions(data || []);
      } catch { /* hide */ }
    })();
  }, [id]);

  useEffect(() => {
    if (!article?.author) { setAuthorProfile(null); return; }
    const slug = article.author.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    (async () => {
      try {
        const { data } = await api.get(`/authors/${slug}`);
        setAuthorProfile(data || null);
      } catch { setAuthorProfile(null); }
    })();
  }, [article?.author]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/comments`, { params: { thread_id: `news-${id}` } });
        setComments(data || []);
      } catch (err) {
        console.error("NewsDetail comments load failed:", err);
      }
    })();
  }, [id]);

  // Inject NewsArticle JSON-LD structured data for SEO (Google News, Discover, rich results).
  // Follows schema.org/NewsArticle + Google's structured-data guidelines.
  useEffect(() => {
    if (!article) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://thetitanstandard.com";
    const absUrl = (u) => {
      if (!u) return undefined;
      if (u.startsWith("http")) return u;
      if (u.startsWith("/")) return `${origin}${u}`;
      return u;
    };
    // Plain-text body from block content (paragraphs, headings, quotes, HTML) + fallback body.
    const blockText = (article.blocks || [])
      .map((b) => {
        if (!b) return "";
        if (b.type === "html" && b.html) {
          const div = typeof document !== "undefined" ? document.createElement("div") : null;
          if (div) { div.innerHTML = b.html; return (div.textContent || "").trim(); }
          return "";
        }
        if (b.type === "paragraph" || b.type === "heading" || b.type === "quote" || !b.type) {
          return (b.text || "").replace(/\*\*|\+\+|\*/g, "").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
        }
        return "";
      })
      .filter(Boolean)
      .join("\n\n")
      .trim();
    const articleBody = (blockText || article.body || "").trim();
    const wordCount = articleBody ? articleBody.split(/\s+/).filter(Boolean).length : undefined;

    // Google News caps headline at 110 chars for rich-result eligibility.
    const rawHeadline = (article.seo_title || article.headline || "").trim();
    const headline = rawHeadline.length > 110 ? `${rawHeadline.slice(0, 107)}…` : rawHeadline;

    // Collect every image referenced in the article (hero + inline blocks + inside html blocks).
    const imageUrls = [];
    if (article.image) imageUrls.push(absUrl(resolveImg(article.image)));
    (article.blocks || []).forEach((b) => {
      if (b?.type === "image" && b.url) {
        const u = absUrl(resolveImg(b.url));
        if (u && !imageUrls.includes(u)) imageUrls.push(u);
      }
      if (b?.type === "html" && b.html && typeof document !== "undefined") {
        const div = document.createElement("div");
        div.innerHTML = b.html;
        div.querySelectorAll("img[src]").forEach((img) => {
          const u = absUrl(img.getAttribute("src"));
          if (u && !imageUrls.includes(u)) imageUrls.push(u);
        });
      }
    });

    // Keywords: editor tags first (player names, topics), then category + subcategory.
    const tagList = Array.isArray(article.tags) ? article.tags.filter(Boolean) : [];
    const keywords = [...tagList, article.category, article.subcategory].filter(Boolean).join(", ") || undefined;
    // `about` entities tell Google the article is ABOUT these people/topics (entity SEO).
    const aboutEntities = tagList.slice(0, 10).map((t) => ({ "@type": "Thing", name: t }));

    // Author slug for canonical author profile URL (matches /author/:slug route).
    const authorName = (article.author || "").trim() || "The Titan Standard Staff";
    const authorSlug = authorName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const authorUrl = authorSlug ? `${origin}/author/${authorSlug}` : undefined;

    // Dates: NewsArticle requires datePublished; dateModified falls back to published.
    const datePublished = article.published || article.created_at || article.published_at || undefined;
    const dateModified = article.updated_at || article.updated || datePublished;
    const pageUrlEarly = `${origin}/news/${article.slug || article.id}`;

    const ld = {
      "@context": "https://schema.org",
      "@type": article.original_reporting ? ["SportsArticle", "Report"] : "SportsArticle",
      "@id": `${pageUrlEarly}#article`,
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrlEarly },
      url: pageUrlEarly,
      headline,
      description: (article.meta_description || article.description || "").trim().slice(0, 300) || undefined,
      image: imageUrls.length ? imageUrls : undefined,
      thumbnailUrl: imageUrls[0] || undefined,
      // Google Discover reads `additionalType` for original-reporting signal.
      additionalType: article.original_reporting ? "https://schema.org/Report" : undefined,
      datePublished,
      dateModified,
      author: {
        "@type": "Person",
        name: authorName,
        url: authorUrl,
      },
      publisher: {
        "@type": "Organization",
        name: "The Titan Standard",
        url: origin,
        logo: {
          "@type": "ImageObject",
          url: `${origin}/logo512.png`,
          width: 512,
          height: 512,
        },
      },
      articleSection: article.category || undefined,
      keywords,
      about: aboutEntities.length ? aboutEntities : undefined,
      articleBody: articleBody || undefined,
      wordCount,
      inLanguage: "en-US",
      isAccessibleForFree: true,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", '[data-testid^="article-block-paragraph-"]'],
      },
    };
    // Strip undefined/null keys for clean output.
    const clean = JSON.parse(JSON.stringify(ld));
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "article-jsonld";
    el.text = JSON.stringify(clean);
    document.head.appendChild(el);

    // Breadcrumb JSON-LD — Home → Category (if known) → Article.
    // Enables Google's breadcrumb rich results on SERPs.
    const categoryPath = {
      "Breaking News": "/breaking-news",
      "Analysis": "/analysis",
      "Offseason": "/offseason",
    }[article.category] || "/news";
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: article.category || "News", item: `${origin}${categoryPath}` },
        { "@type": "ListItem", position: 3, name: (article.headline || "").slice(0, 90), item: pageUrlEarly },
      ],
    };
    const bcEl = document.createElement("script");
    bcEl.type = "application/ld+json";
    bcEl.id = "article-breadcrumb-jsonld";
    bcEl.text = JSON.stringify(breadcrumbLd);
    document.head.appendChild(bcEl);

    // Update document title + per-article meta tags (description, OG, Twitter).
    const prevTitle = document.title;
    const seoTitle = (article.seo_title || article.headline || "").trim();
    document.title = seoTitle ? `${seoTitle} · The Titan Standard` : "The Titan Standard";
    const desc = (article.meta_description || article.description || "").trim().slice(0, 170);
    const heroImg = article.image ? absUrl(resolveImg(article.image)) : `${origin}/titan-standard-logo.png`;
    const pageUrl = `${origin}/news/${article.slug || article.id}`;

    const upsertMeta = (selector, attrs) => {
      let m = document.querySelector(selector);
      if (!m) {
        m = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => { if (k !== "content") m.setAttribute(k, v); });
        document.head.appendChild(m);
      }
      m.setAttribute("content", attrs.content || "");
      m.setAttribute("data-article-meta", "1");
      return m;
    };
    upsertMeta('meta[name="description"]', { name: "description", content: desc });
    // news_keywords — the meta tag Google News reads for topical relevance.
    if (keywords) {
      upsertMeta('meta[name="news_keywords"]', { name: "news_keywords", content: keywords });
      upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    }
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: seoTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: heroImg });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: pageUrl });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "article" });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: seoTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: heroImg });

    // Also set a canonical <link> so Google prefers our URL even if it's
    // accessed via params. Reuse ANY existing <link rel="canonical"> so we
    // don't ship two conflicting canonicals (Google would then pick one).
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.id = "article-canonical";
    canonical.href = pageUrl;
    return () => {
      document.title = prevTitle;
      document.getElementById("article-jsonld")?.remove();
      document.getElementById("article-breadcrumb-jsonld")?.remove();
      document.getElementById("article-canonical")?.remove();
      // Clean up the per-article meta tags we injected.
      document.querySelectorAll('meta[data-article-meta="1"]').forEach((m) => m.remove());
    };
  }, [article]);

  const submit = async (e) => {
    e.preventDefault();
    if (!author || !body) return;
    try {
      const { data } = await api.post("/comments", { thread_id: `news-${id}`, author, body });
      track("comment_post", { thread: `news-${id}` });
      setComments((c) => [data, ...c]);
      setBody("");
      toast.success("Comment posted");
    } catch {
      toast.error("Failed to post");
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-5 py-16 text-titans-silver">Loading…</div>;
  if (!article) return (
    <div className="max-w-3xl mx-auto px-5 py-16">
      <Link to="/news" className="text-titans-blue font-cond uppercase tracking-wider inline-flex items-center gap-2">
        <ArrowLeft size={14} /> Back to news
      </Link>
      <div className="text-titans-silver mt-6">Article not found.</div>
    </div>
  );

  return (
    <div data-testid="news-detail-page">
      {/* Reading progress bar — sticky under the nav, fills as reader scrolls the body. */}
      <ReadingProgressBar targetRef={articleBodyRef} />

      {/* CINEMATIC HERO — full-bleed image sits as its OWN block below the
          top nav (not embedded under it). Wrapper adds breathing room so the
          hero feels like a distinct panel. */}
      <div className="bg-titans-void pt-4 md:pt-6 pb-1">
      <header
        className="relative overflow-hidden bg-titans-void max-w-[1400px] mx-auto rounded-sm md:rounded"
        data-testid="news-hero"
        style={{ minHeight: "clamp(420px, 58vh, 680px)" }}
      >
        {article.image ? (
          <img
            src={resolveImg(article.image) || FALLBACK}
            alt={article.headline}
            data-testid="news-hero-image"
            className="absolute inset-0 w-full h-full object-cover crisp"
            style={{ objectPosition: article.hero_focus || "center 22%" }}
            fetchPriority="high"
            decoding="async"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-titans-navy via-titans-void to-black" />
        )}
        {/* Cinematic overlays: bottom-anchored dark gradient for legibility + faint red vignette on right. */}
        <div className="absolute inset-0 bg-gradient-to-t from-titans-void via-titans-void/80 via-40% to-titans-void/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-titans-void/50 via-transparent to-transparent" />
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-titans-blue via-white to-titans-red" />

        <div className="relative min-h-[inherit] px-5 md:px-10 flex flex-col justify-end pt-14 md:pt-16 pb-10 md:pb-14 text-white">
          <Link to="/news" data-testid="news-back" className="text-titans-blue font-cond uppercase tracking-wider text-sm inline-flex items-center gap-2 mb-6 hover:text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
            <ArrowLeft size={14} /> All News
          </Link>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span
              className={`font-cond uppercase tracking-wider text-[10px] px-2.5 py-1 ${
                article.category === "Breaking News"
                  ? "breaking-chip"
                  : "bg-titans-red text-white"
              }`}
              data-testid="article-category-chip"
            >
              {article.category || "Story"}
            </span>
            {article.original_reporting && (
              <span
                className="inline-flex items-center gap-1.5 font-cond font-bold uppercase tracking-[0.16em] text-[10px] leading-none px-2 py-1 rounded-full bg-emerald-500 text-white shadow-sm"
                data-testid="original-reporting-chip"
                title="Contains first-hand reporting from our staff — interviews, on-scene notes, or scoops we broke."
              >
                <BadgeCheck size={11} strokeWidth={2.5} /> Original Reporting
              </span>
            )}
          </div>
          {/* ADAPTIVE HEADLINE — data-length lets CSS scale font size for
              short/medium/long/verylong headlines. `text-wrap: balance` (all
              modern browsers, degrades gracefully) redistributes words across
              lines so we never get 1-word orphan lines. */}
          <h1
            className="article-hero-headline font-display uppercase tracking-wide drop-shadow-[0_2px_14px_rgba(0,0,0,0.85)]"
            data-length={
              !article.headline ? "medium"
                : article.headline.length <= 32 ? "short"
                : article.headline.length <= 60 ? "medium"
                : article.headline.length <= 90 ? "long"
                : "verylong"
            }
            data-testid="article-headline"
          >
            {article.headline}
          </h1>
          {/* Smaller subtitle underneath */}
          {article.description && (
            <p className="mt-5 text-lg md:text-xl text-titans-silver max-w-3xl leading-snug font-body drop-shadow-[0_1px_6px_rgba(0,0,0,0.85)]" data-testid="article-subtitle">
              {article.description}
            </p>
          )}
          {/* Meta byline: By [Name] • [Date] • [Read time] */}
          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-titans-silver font-cond drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]" data-testid="article-meta-byline">
            {authorProfile?.photo ? (
              <Link to={`/author/${authorProfile.slug}`} className="shrink-0">
                <img src={authorProfile.photo} alt={authorProfile.name} className="w-9 h-9 object-cover border border-white/40" loading="lazy" decoding="async" />
              </Link>
            ) : (
              article.author && (
                <div className="w-9 h-9 shrink-0 bg-titans-blue/25 border border-titans-blue/60 flex items-center justify-center font-display text-titans-blue">
                  {(article.author || "?")[0].toUpperCase()}
                </div>
              )
            )}
            {article.author && (
              <span>
                By{" "}
                {authorProfile ? (
                  <Link to={`/author/${authorProfile.slug}`} className="text-white underline decoration-white/25 hover:decoration-titans-blue" data-testid="article-byline-author-link">
                    {article.author}
                  </Link>
                ) : (
                  <span className="text-white">{article.author}</span>
                )}
              </span>
            )}
            {article.published && (<><span aria-hidden="true">•</span><span>{formatDate(article.published)}</span></>)}
            <span aria-hidden="true">•</span>
            <span data-testid="article-read-time" className="inline-flex items-center gap-1"><Clock size={12} /> {readTime} min read</span>
          </div>
          {/* Emoji reactions right below the meta line — small but noticeable. */}
          <div className="mt-4" data-testid="article-title-reactions">
            <ReactionBar target_type="article" target_id={article.id} size="sm" />
          </div>
        </div>
      </header>
      </div>

      <TitanPulseStrip />

      <div className="article-light">
        <div className="article-shell max-w-[1400px] mx-auto px-5 md:px-8 py-10">
        <article ref={articleBodyRef} className="article-column article-body" data-testid="article-column">
          {/* Quick-hit AI summary — shown above the body. */}
          <QuickHitsBox articleId={article.id} />

          {/* IN-ARTICLE AD — only rendered once AdSense is configured, so an
              unfilled slot never creates dead space. */}
          {ADS_ENABLED && (
            <div className="my-8" data-testid="in-article-ad-wrapper">
              <AdSlot
                slot={process.env.REACT_APP_ADSENSE_SLOT_IN_ARTICLE || ""}
                format="fluid"
                layout="in-article"
                style={{ display: "block", textAlign: "center", minHeight: 250 }}
                testId="ad-in-article-top"
              />
            </div>
          )}

          {Array.isArray(article.blocks) && article.blocks.length > 0 ? (
            <div className="font-body leading-relaxed text-lg space-y-6" data-testid="article-blocks">
              {article.blocks.map((b, i) => {
                if (b.type === "html" && b.html) {
                  const clean = splitCaptionCredit(DOMPurify.sanitize(b.html, {
                    ADD_ATTR: ["target", "rel", "data-theme", "data-dnt", "data-instgrm-permalink", "data-instgrm-version", "data-video-id", "cite"],
                    ADD_TAGS: ["iframe"],
                  }));
                  return (
                    <div
                      key={i}
                      className="article-html"
                      data-testid={`article-block-html-${i}`}
                      dangerouslySetInnerHTML={{ __html: clean }}
                    />
                  );
                }
                if (b.type === "image" && b.url) {
                  return (
                    <figure key={i} className="my-8 max-w-full" data-testid={`article-block-image-${i}`}>
                      <div className="relative bg-titans-void p-2 border-2 border-titans-blue/70 shadow-[0_14px_38px_-18px_rgba(0,0,0,0.7)] w-full max-w-full overflow-hidden">
                        <img
                          src={resolveImg(b.url)}
                          alt={b.alt || b.caption || ""}
                          className="block w-full h-auto max-w-full object-contain"
                        />
                      </div>
                      {(b.caption || b.credit) && (
                        <figcaption className="article-figcaption" data-testid={`article-block-caption-${i}`}>
                          {b.caption && <span>{b.caption}</span>}
                          {b.credit && <span className="ts-photo-credit">Mandatory Credit: {b.credit}</span>}
                        </figcaption>
                      )}
                    </figure>
                  );
                }
                if (b.type === "video" && b.url) {
                  return (
                    <VideoEmbed
                      key={i}
                      url={b.url}
                      caption={b.caption}
                      credit={b.credit}
                      testid={`article-block-video-${i}`}
                    />
                  );
                }
                if (b.type === "heading" && (b.text || "").trim()) {
                  return (
                    <h2 key={i} data-testid={`article-block-heading-${i}`} className="font-display text-2xl md:text-3xl uppercase tracking-wide mt-8">
                      <RichText text={b.text} />
                    </h2>
                  );
                }
                if (b.type === "quote" && (b.text || "").trim()) {
                  return (
                    <blockquote key={i} data-testid={`article-block-quote-${i}`} className="border-l-4 border-titans-red pl-5 italic text-xl my-6">
                      <RichText text={b.text} />
                    </blockquote>
                  );
                }
                if ((b.type === "paragraph" || !b.type) && (b.text || "").trim()) {
                  return (
                    <p key={i} data-testid={`article-block-paragraph-${i}`} className="whitespace-pre-wrap">
                      <RichText text={b.text} />
                    </p>
                  );
                }
                return null;
              })}
            </div>
          ) : article.body ? (
            <div className="font-body leading-relaxed whitespace-pre-wrap">
              {article.body}
            </div>
          ) : null}

          {/* Outbound source citation */}
          {article.outbound_source_url && (
            <aside
              data-testid="article-source"
              className="mt-10 titans-card p-5 border-l-4 border-titans-blue flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            >
              <div>
                <div className="font-cond uppercase tracking-[0.22em] text-[10px] text-titans-blue mb-1">
                  Source
                </div>
                <div className="font-body">
                  {article.outbound_source_label || article.outbound_source_url}
                </div>
              </div>
              <a
                href={article.outbound_source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-titans-blue/20 hover:bg-titans-blue/30 text-titans-blue px-4 py-2 font-cond uppercase tracking-wider text-xs inline-flex items-center gap-2 self-start sm:self-auto"
              >
                Read source ↗
              </a>
            </aside>
          )}

          {/* Topic tags — visible, clickable chips → /tag pages (Google rewards visible tags) */}
          {Array.isArray(article.tags) && article.tags.length > 0 && (
            <div className="mt-10" data-testid="article-tags">
              <div className="font-cond uppercase tracking-[0.22em] text-[10px] text-titans-silver mb-3">
                Tagged In
              </div>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/tag/${tagSlug(t)}`}
                    data-testid={`article-tag-${tagSlug(t)}`}
                    className="font-cond uppercase tracking-wider text-xs px-3 py-1.5 border border-[rgba(138,141,143,0.35)] text-titans-silver hover:text-white hover:border-titans-blue hover:bg-titans-blue/15 transition-colors"
                  >
                    #{t}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Related articles */}
          {related.length > 0 && (
            <div className="mt-14" data-testid="related-articles">
              <div className="flex items-center gap-2 mb-3" data-reveal data-testid="finished-reading">
                <CheckCircle2 size={14} className="text-emerald-500" />
                <span className="font-cond uppercase tracking-[0.2em] text-[11px] text-emerald-600">Finished reading</span>
                <span className="font-cond uppercase tracking-[0.2em] text-[11px] text-titans-silver">· Recommended next</span>
              </div>
              <h3 className="font-display text-2xl uppercase tracking-wide section-title">
                Keep Reading
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                {related.map((r, i) => (
                  <Link
                    key={r.id}
                    to={`/news/${r.slug || r.id}`}
                    state={{ article: r }}
                    data-testid={`related-article-${i}`}
                    className="group story-card aspect-[4/5] block hover-rise overflow-hidden"
                  >
                    <img
                      src={resolveImg(r.image) || FALLBACK}
                      alt={r.headline}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 story-gradient pointer-events-none" />
                    <div className="absolute inset-x-0 bottom-0 p-3 z-[2]">
                      <div className="font-cond uppercase tracking-[0.18em] text-[9px] text-titans-blue mb-1">
                        {r.category || "Story"}
                      </div>
                      <div className="font-display text-sm uppercase leading-tight text-white group-hover:text-titans-red transition-colors line-clamp-3">
                        {r.headline}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="mt-14" data-testid="article-comments">
            <h3 className="font-display text-2xl uppercase tracking-wide section-title flex items-center gap-2">
              <MessageSquare size={20} /> Fan Reactions ({comments.length})
            </h3>
            <form onSubmit={submit} className="mt-6 titans-card p-5 space-y-3">
              <input
                type="text"
                placeholder="Your name / handle"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                data-testid="comment-author-input"
                className="w-full bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-4 py-3 font-body"
              />
              <textarea
                rows={3}
                placeholder="Drop your hot take…"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                data-testid="comment-body-input"
                className="w-full bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-4 py-3 font-body resize-none"
              />
              <button
                type="submit"
                data-testid="comment-submit"
                className="bg-titans-red hover:bg-titans-red-hover px-5 py-2.5 font-cond uppercase tracking-wider text-sm"
              >
                Post Reaction
              </button>
            </form>
            <div className="mt-6 space-y-4">
              {comments.map((c, i) => (
                <div key={c.id} data-testid={`comment-${i}`} className="titans-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="bg-titans-red text-white w-8 h-8 flex items-center justify-center font-display text-sm">
                      {(c.author || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="font-cond uppercase tracking-wider text-sm">{c.author}</div>
                      <div className="text-titans-muted text-[10px] font-cond uppercase tracking-wider">
                        {formatDate(c.created_at, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                  <div className="font-body">{c.body}</div>
                </div>
              ))}
              {comments.length === 0 && <div className="text-titans-muted text-sm">Be the first to react.</div>}
            </div>
          </div>

          {/* Corrections + policies footer — required for Google News eligibility */}
          <div className="mt-10 pt-6 border-t border-titans-navy/30 flex flex-wrap gap-x-4 gap-y-2 text-[11px] font-cond uppercase tracking-wider text-titans-muted" data-testid="article-integrity-footer">
            <a
              href={`mailto:corrections@thetitanstandard.com?subject=${encodeURIComponent(`Correction — ${article.headline}`)}&body=${encodeURIComponent(`Article: ${typeof window !== "undefined" ? window.location.href : ""}\n\nWhat needs correcting:\n\nSource:\n`)}`}
              className="hover:text-titans-red inline-flex items-center gap-1"
              data-testid="report-correction-link"
            >
              Report a correction
            </a>
            <span>·</span>
            <Link to="/editorial-standards" className="hover:text-titans-blue">Editorial Standards</Link>
            <span>·</span>
            <Link to="/corrections" className="hover:text-titans-blue">Corrections Policy</Link>
            <span>·</span>
            <Link to="/ownership" className="hover:text-titans-blue">Ownership</Link>
          </div>
        </article>

        <aside className="article-sidebar" data-testid="article-sidebar">
          <div
            className="article-sidebar-scroller space-y-5 sidebar-scroll"
            data-testid="article-sidebar-scroller"
          >
            <ShareBar articleId={article.id} headline={article.headline} />
            <UpNextWidget articleId={article.id} />
            {mentions.length > 0 && (
              <div className="titans-card p-5 relative overflow-hidden" data-testid="article-mentioned-players">
                <div className="absolute top-0 left-0 h-1 w-16 bg-titans-red" />
                <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-red mb-3 pt-1">Titans in this story</div>
                <ul className="space-y-2">
                  {mentions.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/player/${p.id}`}
                        data-testid={`article-mention-${p.id}`}
                        className="flex items-center gap-3 group hover:bg-titans-navy/40 -mx-1 px-1 py-1.5 transition-colors"
                      >
                        {p.headshot ? (
                          <img
                            src={p.headshot}
                            alt={p.fullName}
                            loading="lazy"
                            decoding="async"
                            className="w-10 h-10 object-cover object-top bg-titans-void border border-titans-blue/40"
                          />
                        ) : (
                          <div className="w-10 h-10 flex items-center justify-center bg-titans-navy border border-titans-blue/40 font-display text-titans-blue text-sm">
                            {p.position || "—"}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-display uppercase text-white truncate group-hover:text-titans-blue text-sm leading-tight">{p.fullName}</div>
                          <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
                            {p.position || ""}{p.jersey ? ` · #${p.jersey}` : ""}
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <TitanTemperature />
            <AdSlot
              slot={process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR || ""}
              format="rectangle"
              style={{ display: "block", minHeight: 250 }}
              testId="ad-sidebar-article"
            />
            <ArticleSidebar excludeArticleId={article.id} />
          </div>
        </aside>
      </div>

      {/* AUTHOR BIO — magazine-style card at the bottom of every article. */}
      <div className="max-w-[1300px] mx-auto px-5 md:px-10 pb-6">
        <FAQBox articleId={article.id} />
      </div>
      <div className="max-w-[1300px] mx-auto px-5 md:px-10 pb-14">
        <AuthorBioCard author={authorProfile} articleAuthorName={article.author} />
      </div>
      </div>
    </div>
  );
}
